/*
 * School modal · Counsellors table: the column factory, leader maxima, the Team median footer and
 * the CoverageBar meter.
 *
 * Ports LAE `features/productivity/shared/counsellorColumns.jsx` (trimmed to the school modal's
 * Counsellors table, build spec §7.5) and `charts/CoverageBar.jsx`. Column shape follows the §10.3
 * column contract; the table itself (filters, ColumnManager, frozen name, rank numbering, sort) is
 * `SchoolCounsellorsTable.tsx`.
 *
 * Deliberate differences from LAE:
 * - Counsellor names are plain text. There is no counsellor modal, so no row click, no button and
 *   no link (owner 2026-10-29). The native `title` carries the name only (the region is not on the
 *   wire).
 * - No Productivity Score column, no rubric `Banded` dots, no score median (rubric dropped, §0.3).
 * - No call-analysis columns: Student Sentiment, Hot Leads and Call Quality are gone, and so are
 *   their leader keys, because the EMERGE mirror drops those fields.
 * - No Occupancy, Available Hours or Busy Hours columns (nor their footer cells): the Schools
 *   service never computes them (always null / 0), so they would read the same on every row.
 * - No Trend sparkline column (`withTrend = false` in the school modal) and no single-day
 *   First / Last Activity columns (the modal never passes `isSingleDay`).
 * - No drill-down counts and no occupancy modal button: every figure is plain text.
 * - No definition tooltips on headers or on "Team median" (§0.4).
 * - Leader highlight is weight only; LAE used the brand colour, which EMERGE keeps for primary
 *   actions and active state.
 * - `defaultHidden` is not carried: every column starts visible (§10.3 recommendation), because
 *   `useColumnLayout` stores hidden ids only and a faked first-mount hide would be a preference
 *   nobody chose.
 * - Numeric columns carry `kind: 'number'` so the shared filter offers number conditions
 *   (owner 2026-10-29). Duration columns filter in MINUTES, as the Schools table does.
 * - CoverageBar fills with the chart-1 token and is `aria-hidden`: the figure always sits beside
 *   it. A blank value draws an empty spacer of the same width, not a second dash.
 *
 * Idle rows (no calls and nothing worked) are dimmed inside each cell here, so the table must NOT
 * dim the row as well.
 */
import type { ReactNode } from 'react';
import { Badge } from '@/components/ui';
import { cx } from '@/lib/cx';
import type { LaeProdCounsellorRow } from '@/lib/types';
import { fmtDuration, fmtNum, fmtPct, fmtShortDuration, median } from './lae-format';

/** A counsellor row as the table renders it: `__rank` is the 1-based position under the current sort. */
export type CounsellorRow = LaeProdCounsellorRow & { __rank?: number };

/** §10.3 column contract, Counsellors flavour. */
export interface CounsellorColumn {
  /** Stored by ColumnManager and filter memory: never rename. */
  key: string;
  /** Full label: header text, header `title`, ColumnManager `labelOf`. */
  label: string;
  /** Visible header when shorter than the label (unused here: every header is the full label). */
  short?: string;
  /** Right-aligned, tabular figures. */
  numeric?: boolean;
  /** `number` = the shared filter offers number conditions; `get` returns a number or null. */
  kind?: 'number';
  /** Filter value. Durations are minutes. */
  get: (row: CounsellorRow) => string | number | null | undefined;
  /** Sort value; null sorts last in either direction. */
  sortValue: (row: CounsellorRow) => string | number | null;
  render: (row: CounsellorRow) => ReactNode;
  /** false = not listed in the ColumnManager (the rank column). */
  manageable?: boolean;
  /** false = no sort controls (the rank column). */
  sortable?: boolean;
  /** false = no filter popover (the rank column). */
  filterable?: boolean;
  /** Code-pinned left (the name column). */
  pinned?: boolean;
  className?: string;
}

/** Column maxima (> 0) keyed by metric; null when no row has a positive value. */
export type CounsellorLeaders = Record<string, number | null>;

type MetricKey = keyof LaeProdCounsellorRow & string;
type Formatter = (v: number | null | undefined) => string;

const isBlank = (v: unknown): v is null | undefined | '' => v === null || v === undefined || v === '';

const numOf = (v: unknown): number | null => {
  if (isBlank(v)) return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

const isActive = (r: CounsellorRow): boolean => (numOf(r.calls) ?? 0) > 0 || (numOf(r.worked) ?? 0) > 0;

const valueOf = (r: CounsellorRow, key: MetricKey): number | null => numOf(r[key]);

const fmtRate: Formatter = (v) => fmtPct(v);

/** The keys whose column maximum is drawn as the leader. Negative changes are deliberately absent. */
const LEADER_KEYS: readonly MetricKey[] = [
  'calls', 'connected', 'connect_rate', 'call_seconds', 'called_records', 'reached_people',
  'advanced', 'counselling_completed', 'applications', 'matriculation', 'tasks_done', 'active_days', 'coverage_pct',
  'avg_talk_seconds', 'ccm_talk_seconds', 'avg_ccm_talk_seconds', 'cc_talk_seconds', 'avg_cc_talk_seconds',
  'adv_talk_seconds', 'avg_adv_talk_seconds', 'whatsapp_prepared', 'emails_sent',
];

/** Column maxima (> 0 only) for the leader highlight. */
export function counsellorLeaders(rows: readonly CounsellorRow[] | null | undefined): CounsellorLeaders {
  const list = Array.isArray(rows) ? rows : [];
  const out: CounsellorLeaders = {};
  for (const k of LEADER_KEYS) {
    let m = 0;
    for (const r of list) {
      const v = valueOf(r, k);
      if (v !== null && v > m) m = v;
    }
    out[k] = m > 0 ? m : null;
  }
  return out;
}

/** Thin 4px meter beside a percentage. Decorative: the figure is always next to it. */
export function CoverageBar({
  pct,
  width = 64,
  className,
}: {
  pct: number | null | undefined;
  width?: number;
  className?: string;
}) {
  const n = numOf(pct);
  if (n === null) {
    return <span aria-hidden="true" className={cx('inline-block h-1 shrink-0', className)} style={{ width }} />;
  }
  const clamped = Math.max(0, Math.min(100, n));
  return (
    <span
      aria-hidden="true"
      className={cx('relative inline-block h-1 shrink-0 overflow-hidden rounded-full bg-surface-sunken', className)}
      style={{ width }}
    >
      <span className="absolute inset-y-0 left-0 rounded-full bg-chart-1" style={{ width: `${clamped}%` }} />
    </span>
  );
}

/** A figure: zero and blank faint, the leader in weight, an idle row dimmed. */
function Figure({ value, text, leader, dim }: { value: number | null; text: string; leader?: boolean; dim?: boolean }) {
  return (
    <span
      className={cx(
        'tabular-nums whitespace-nowrap',
        value === null || value === 0 ? 'text-fg-subtle' : leader ? 'font-semibold text-fg' : 'text-fg',
        dim && 'opacity-60',
      )}
    >
      {text}
    </span>
  );
}

interface MetricDef {
  key: MetricKey;
  label: string;
  fmt: Formatter;
  /** Seconds: filter in minutes. */
  duration?: boolean;
  /** Draw a CoverageBar of this width beside the figure. */
  bar?: number;
}

const DURATION = { duration: true } as const;

function metricDefs(admit: boolean): MetricDef[] {
  return [
    { key: 'calls', label: 'Total Calls Dialled', fmt: fmtNum },
    { key: 'manual_calls', label: 'Manual Calls', fmt: fmtNum },
    { key: 'connected', label: 'Calls Answered', fmt: fmtNum },
    { key: 'manual_connected', label: 'Manual Connected', fmt: fmtNum },
    { key: 'manual_seconds', label: 'Manual Talk Time', fmt: fmtDuration, ...DURATION },
    { key: 'connect_rate', label: 'Answer Rate', fmt: fmtRate },
    { key: 'call_seconds', label: 'Total Call Time', fmt: fmtDuration, ...DURATION },
    { key: 'avg_talk_seconds', label: 'Avg Call Time', fmt: fmtShortDuration, ...DURATION },
    { key: 'ccm_talk_seconds', label: 'Total Call Time - Interested in 2027 Intakes', fmt: fmtDuration, ...DURATION },
    { key: 'avg_ccm_talk_seconds', label: 'Avg Call Time - Interested in 2027 Intakes', fmt: fmtShortDuration, ...DURATION },
    { key: 'cc_talk_seconds', label: 'Talk Time - Already Interested in 2027 Intakes', fmt: fmtDuration, ...DURATION },
    { key: 'avg_cc_talk_seconds', label: 'Avg Talk Time - Already Interested in 2027 Intakes', fmt: fmtShortDuration, ...DURATION },
    { key: 'adv_talk_seconds', label: 'Total Call Time - Positive Changes', fmt: fmtDuration, ...DURATION },
    { key: 'avg_adv_talk_seconds', label: 'Avg Call Time - Positive Changes', fmt: fmtShortDuration, ...DURATION },
    { key: 'neg_talk_seconds', label: 'Total Call Time - Negative Changes', fmt: fmtDuration, ...DURATION },
    { key: 'avg_neg_talk_seconds', label: 'Avg Call Time - Negative Changes', fmt: fmtShortDuration, ...DURATION },
    { key: 'called_records', label: 'Leads Called', fmt: fmtNum },
    { key: 'reached_people', label: 'Leads Spoken To', fmt: fmtNum },
    { key: 'advanced', label: 'Positive Status Changes', fmt: fmtNum },
    { key: 'counselling_completed', label: 'Marked as Interested in 2027 Intakes', fmt: fmtNum },
    { key: 'regressed', label: 'Negative Status Changes', fmt: fmtNum },
    admit
      ? { key: 'matriculation', label: 'Reached Deposit / Visa', fmt: fmtNum }
      : { key: 'applications', label: 'Applications', fmt: fmtNum },
    { key: 'whatsapp_prepared', label: 'Whatsapps Sent', fmt: fmtNum },
    { key: 'emails_sent', label: 'Emails Sent', fmt: fmtNum },
    { key: 'tasks_done', label: 'Tasks Completed', fmt: fmtNum },
    { key: 'active_days', label: 'Days Active', fmt: fmtNum },
    { key: 'coverage_pct', label: '% Contacted', fmt: fmtRate, bar: 48 },
  ];
}

function figureWithBar(text: ReactNode, pct: number | null, width: number | undefined, dim?: boolean): ReactNode {
  if (width === undefined) return text;
  return (
    <span className={cx('inline-flex items-center justify-end gap-2', dim && 'opacity-60')}>
      {text}
      <CoverageBar pct={pct} width={width} />
    </span>
  );
}

/**
 * The Counsellors table's columns, in LAE order.
 * - `admit`: the admit-to-enrolment funnel swaps Applications for Reached Deposit / Visa.
 * - `max`: `counsellorLeaders(rows)`.
 * - `presentKeys`: metric keys that are not `undefined` on at least one row. A metric column whose
 *   key is missing is omitted, so an older API never draws a column of dashes. Omitted = keep all.
 *   Rank and name are always kept.
 */
export function createCounsellorColumns({
  admit = false,
  max = {},
  presentKeys,
}: {
  admit?: boolean;
  max?: CounsellorLeaders;
  presentKeys?: ReadonlySet<string>;
} = {}): CounsellorColumn[] {
  const isLeader = (key: MetricKey, v: number | null): boolean => {
    const m = max[key];
    return m !== undefined && m !== null && v !== null && v === m;
  };

  const cols: CounsellorColumn[] = [
    {
      key: 'rank',
      label: '#',
      numeric: true,
      manageable: false,
      sortable: false,
      filterable: false,
      get: (r) => r.__rank ?? null,
      sortValue: (r) => r.__rank ?? null,
      render: (r) => (
        <span className={cx('tabular-nums text-fg-subtle', !isActive(r) && 'opacity-60')}>
          {r.__rank ?? '—'}
        </span>
      ),
      className: 'w-10',
    },
    {
      key: 'name',
      label: 'Counsellor',
      pinned: true,
      get: (r) => r.name,
      sortValue: (r) => (isBlank(r.name) ? null : r.name),
      render: (r) => (
        <div className={cx('flex min-w-[160px] max-w-[240px] items-center gap-2', !isActive(r) && 'opacity-60')}>
          <span className="truncate font-medium text-fg" title={r.name || undefined}>
            {r.name || '—'}
          </span>
          {r.is_current_rep === false && (
            <Badge tone="neutral" className="shrink-0">
              Former
            </Badge>
          )}
        </div>
      ),
    },
  ];

  for (const def of metricDefs(admit)) {
    if (presentKeys && !presentKeys.has(def.key)) continue;
    const { key, label, fmt, duration, bar } = def;
    cols.push({
      key,
      label,
      numeric: true,
      kind: 'number',
      get: (r) => {
        const v = valueOf(r, key);
        return v === null ? null : duration ? v / 60 : v;
      },
      sortValue: (r) => valueOf(r, key),
      render: (r) => {
        const v = valueOf(r, key);
        const dim = !isActive(r);
        if (bar === undefined) {
          return <Figure value={v} text={fmt(v)} leader={isLeader(key, v)} dim={dim} />;
        }
        return figureWithBar(<Figure value={v} text={fmt(v)} leader={isLeader(key, v)} />, v, bar, dim);
      },
    });
  }

  return cols;
}

/**
 * Team median footer cells, keyed by column key: the median of each metric over ACTIVE rows
 * (nulls ignored). Null when no row is active, so the table draws no footer.
 */
export function counsellorFooterCells(
  rows: readonly CounsellorRow[] | null | undefined,
  { admit = false }: { admit?: boolean } = {},
): Record<string, ReactNode> | null {
  const list = Array.isArray(rows) ? rows : [];
  const active = list.filter(isActive);
  if (active.length === 0) return null;

  const cells: Record<string, ReactNode> = {
    name: <span className="font-medium text-fg">Team median</span>,
  };
  for (const { key, fmt, bar } of metricDefs(admit)) {
    const vals = active.map((r) => valueOf(r, key)).filter((v): v is number => v !== null);
    const m = vals.length ? median(vals) : null;
    const v = numOf(m);
    cells[key] = figureWithBar(<Figure value={v} text={fmt(v)} />, v, bar);
  }
  return cells;
}
